// The single-clinic client's brand, centred at the top of the sidebar: the practice's
// logo alone, in place of the Atlas logo and the practice picker. The name shows only
// when there is no logo, so the header is never empty. Rendered by
// Sidebar only for a client with one clinic (or a super_admin previewing one practice
// with "View as client"); a client with several clinics keeps the picker.
//
// The logo is fetched as a blob through window.fetch, never put in an
// <img src="/api/...">, for the same reason PracticeLogoCard does it: the Clerk JWT
// rides only on fetch, so a plain image request would arrive unsigned. A 404 means
// the practice has no logo and the brand shows the name instead; it never shows an
// error message, because a client cannot act on one. Fetched once per practice id.

import React, { useEffect, useState } from 'react';

import './ClientBrand.css';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ClientBrand({ practice }) {
  const practiceId = practice ? practice.id : null;
  const [src, setSrc] = useState(null); // object URL of the stored logo

  useEffect(() => {
    if (!practiceId) return undefined;
    let cancelled = false;
    let objectUrl = null;
    setSrc(null);

    const load = async () => {
      try {
        const response = await fetch(`${API_URL}/api/practices/${practiceId}/logo`, {
          credentials: 'include',
        });
        if (cancelled) return;
        if (response.status === 404) return; // no logo: name only
        if (!response.ok) {
          // Not the no-logo case. The brand falls back to the name — a client has
          // nothing to do about a failed image — but the failure is not swallowed.
          console.warn(`Practice logo request failed: ${response.status}`);
          return;
        }
        const blob = await response.blob();
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        setSrc(objectUrl);
      } catch (err) {
        if (!cancelled) console.warn('Practice logo request failed', err);
      }
    };
    load();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [practiceId]);

  if (!practice) return null;

  return (
    <div className="client-brand">
      {src ? (
        <img className="client-brand-logo" src={src} alt={`${practice.name} logo`} />
      ) : (
        <div className="client-brand-name">{practice.name}</div>
      )}
    </div>
  );
}
